import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnInit,
  WritableSignal,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import {
  NgbActiveModal,
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbDatepickerModule,
} from '@ng-bootstrap/ng-bootstrap';
import { map, startWith } from 'rxjs';
import {
  DEFAULT_MAX_RESULT_COUNT,
  DateMaskDirective,
  PERIOD_TYPE_NAME_PLANNED_PLANNING_SEASON,
  REASON_GROUP_NAME_STAND_STATE,
  ZendraLocalizationServices,
  ZnConfirmation,
  ZnSelectQueryParams,
  ZnSingleSelect,
  dismissDirtyFormModal,
  handleDateBlur,
} from '@zendra/shared';

import type { IModalContent } from '../../../../../shared/src/lib/zn-modal-wrapper/interfaces/IModalContent';
import { ZnSimpleSelect } from '../../../../../shared/src/lib/zn-select/zn-simple-select';
import { CustomDateParserFormatter } from '../../../../../shared/src/lib/utils/custom-date-parser-formatter';
import type { SpeciesDto } from 'projects/cbd-proxies/src/lib/general/dtos/species/models';
import { VendorService } from 'projects/cbd-proxies/src/lib/controlling/controllers/vendor.service';
import {
  GeneticMaterialSelectOption,
  StandCatalogs,
} from '../../stand/stand-creation-form/services/stand-catalogs';
import { syncSpeciesWithGeneticMaterialFromForm } from '../../stand/helpers/genetic-material-species.helper';
import type { AttributeInheritanceFormValue } from './step-define-attribute-inheritance.models';

type SelectOption = { key: string; value: string };
type SelectOptionKey = string | number;
type ResolvableSelectOption = { key: SelectOptionKey; value: string };
type SelectOptionValue = SelectOptionKey | null | undefined;

@Component({
  selector: 'lib-attribute-inheritance-modal',
  standalone: true,
  imports: [
    CommonModule,
    LocalizationPipe,
    ReactiveFormsModule,
    NgbDatepickerModule,
    DateMaskDirective,
    ZnSingleSelect,
    ZnSimpleSelect,
  ],
  templateUrl: './attribute-inheritance-modal.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }],
})
export class AttributeInheritanceModal implements OnInit, IModalContent {
  @Input() standName = '';
  @Input() initialValue: AttributeInheritanceFormValue | null = null;

  private readonly formBuilder = inject(FormBuilder);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly confirmationService = inject(ZnConfirmation);
  private readonly destroyRef = inject(DestroyRef);
  private readonly standCatalogs = inject(StandCatalogs);
  private readonly localizationService = inject(LocalizationService);
  private readonly vendorService = inject(VendorService);

  readonly localizationResource = ZendraLocalizationServices.LanguageService;
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly standTypeOptions = signal<SelectOption[]>([]);
  readonly plantingTypeOptions = signal<SelectOption[]>([]);
  readonly geneticMaterialOptions = signal<GeneticMaterialSelectOption[]>([]);
  readonly selectedPlantationSpecies = signal<SpeciesDto | null>(null);
  private readonly pendingGeneticMaterialRequests = new Set<string>();

  readonly nearbyPopulationOptions: SelectOption[] = [
    { key: 'true', value: 'Option:Yes' },
    { key: 'false', value: 'Option:No' },
  ];

  readonly seasonOptions = signal<SelectOption[]>([]);
  readonly contractorOptions = signal<SelectOption[]>([]);
  readonly standStateOptions = signal<SelectOption[]>([]);
  private readonly standTypeOptionsMap = new Map<string, SelectOption>();
  private readonly plantingTypeOptionsMap = new Map<string, SelectOption>();
  private readonly geneticMaterialOptionsMap = new Map<string, GeneticMaterialSelectOption>();
  private readonly seasonOptionsMap = new Map<string, SelectOption>();
  private readonly contractorOptionsMap = new Map<string, SelectOption>();
  private readonly standStateOptionsMap = new Map<string, SelectOption>();

  readonly form = this.formBuilder.group({
    StandTypeId: [null as string | null],
    HasNearbyPopulation: [null as string | null],
    CurrentRotation: [null as string | null],
    PlantingDate: [null as NgbDateStruct | null],
    PlantingType: [null as string | null],
    GeneticMaterial: [null as string | null],
    Species: [{ value: '', disabled: true }],
    Season: [null as string | null],
    Contractor: [null as string | null],
    StandState: [null as string | null],
  });

  private readonly formValue = toSignal(
    this.form.valueChanges.pipe(startWith(this.form.getRawValue())),
    { initialValue: this.form.getRawValue() },
  );
  private readonly formStatus = toSignal(this.form.statusChanges.pipe(startWith(this.form.status)), {
    initialValue: this.form.status,
  });

  readonly canSave = computed(() => {
    this.formStatus();
    return this.form.valid && !this.loading() && !this.saving();
  });

  constructor() {
    effect(() => {
      this.formValue();
      this.syncSelectedGeneticMaterialSpecies();
    });
  }

  readonly loadSeasonOptions = (params?: ZnSelectQueryParams) => {
    const normalizedParams = this.normalizeLoaderParams(params);
    if (!normalizedParams.preferCache) {
      delete normalizedParams.ids;
    }

    return this.standCatalogs
      .loadPeriod(normalizedParams, PERIOD_TYPE_NAME_PLANNED_PLANNING_SEASON)
      .pipe(
        map(response => {
          this.mergeCatalogOptions(this.seasonOptionsMap, this.seasonOptions, response.items);
          return response;
        }),
      );
  };

  readonly loadStandStateOptions = (params?: ZnSelectQueryParams) =>
    this.standCatalogs
      .loadReason(this.normalizeLoaderParams(params), REASON_GROUP_NAME_STAND_STATE)
      .pipe(
        map(response => {
          this.mergeCatalogOptions(
            this.standStateOptionsMap,
            this.standStateOptions,
            response.items,
          );
          return response;
        }),
      );

  readonly loadStandTypeOptions = (params?: ZnSelectQueryParams) =>
    this.standCatalogs.loadsSandType(this.normalizeLoaderParams(params)).pipe(
      map(response => {
        this.mergeCatalogOptions(this.standTypeOptionsMap, this.standTypeOptions, response.items);
        return response;
      }),
    );

  readonly loadPlantingTypeOptions = (params?: ZnSelectQueryParams) =>
    this.standCatalogs.loadPlantingTypes(this.normalizeLoaderParams(params)).pipe(
      map(response => {
        this.mergeCatalogOptions(
          this.plantingTypeOptionsMap,
          this.plantingTypeOptions,
          response.items,
        );
        return response;
      }),
    );

  readonly loadGeneticMaterialOptions = (params?: ZnSelectQueryParams) =>
    this.standCatalogs.loadGeneticMaterials(this.normalizeLoaderParams(params)).pipe(
      map(response => {
        this.mergeGeneticMaterialOptions(response.items);
        return response;
      }),
    );

  readonly loadContractorOptions = (params?: ZnSelectQueryParams) => {
    const normalizedParams = this.normalizeLoaderParams(params);

    return this.vendorService
      .getList({
        fantasyName: normalizedParams.search,
        skipCount: normalizedParams.skipCount,
        maxResultCount: normalizedParams.maxResultCount,
      })
      .pipe(
        map(response => {
          const items = (response.items ?? []).map(contractor => ({
            key: String(contractor.id),
            value: contractor.fantasyName ?? String(contractor.id),
          }));
          this.mergeCatalogOptions(this.contractorOptionsMap, this.contractorOptions, items);
          return {
            items,
            totalCount: response.totalCount ?? 0,
          };
        }),
      );
  };

  ngOnInit(): void {
    this.patchInitialValue(this.initialValue);
  }

  fieldInvalid(field: string): boolean {
    const control = this.form.get(field);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  onPlantingDateBlur(inputElement: HTMLInputElement): void {
    handleDateBlur(inputElement, this.form.get('PlantingDate'));
  }

  confirm(): void {
    this.save();
  }

  dismiss(): void {
    dismissDirtyFormModal(
      this.form,
      this.saving(),
      this.confirmationService,
      this.activeModal,
    );
  }

  save(): void {
    if (this.form.invalid || this.loading() || this.saving()) {
      this.form.markAllAsTouched();
      return;
    }

    const rawValue = this.form.getRawValue();
    const configuration: AttributeInheritanceFormValue = {
      standTypeId: this.normalizeString(rawValue.StandTypeId),
      standTypeLabel: this.resolveOptionLabel(this.standTypeOptions(), rawValue.StandTypeId),
      nearbyPopulation: this.normalizeString(rawValue.HasNearbyPopulation),
      nearbyPopulationLabel: this.resolveLocalizedOptionLabel(
        this.nearbyPopulationOptions,
        rawValue.HasNearbyPopulation,
        this.localizationResource,
      ),
      rotation: String(rawValue.CurrentRotation ?? '').trim(),
      plantingDate: this.formatDate(rawValue.PlantingDate),
      plantingTypeId: this.normalizeString(rawValue.PlantingType),
      plantingTypeLabel: this.resolveOptionLabel(this.plantingTypeOptions(), rawValue.PlantingType),
      geneticMaterialId: this.normalizeString(rawValue.GeneticMaterial),
      geneticMaterialLabel: this.resolveOptionLabel(
        this.geneticMaterialOptions(),
        rawValue.GeneticMaterial,
      ),
      speciesId: this.normalizeString(this.selectedPlantationSpecies()?.id),
      speciesLabel: this.selectedPlantationSpecies()?.name ?? '',
      season: this.normalizeString(rawValue.Season),
      seasonLabel: this.resolveOptionLabel(this.seasonOptions(), rawValue.Season),
      contractor: this.normalizeString(rawValue.Contractor),
      contractorLabel: this.resolveOptionLabel(this.contractorOptions(), rawValue.Contractor),
      standState: this.normalizeString(rawValue.StandState),
      standStateLabel: this.resolveOptionLabel(
        this.standStateOptions(),
        rawValue.StandState,
        this.resolveInitialOptionLabel(
          rawValue.StandState,
          this.initialValue?.standState,
          this.initialValue?.standStateLabel ?? '',
        ),
      ),
    };

    this.form.markAsPristine();
    this.activeModal.close(configuration);
  }

  private patchInitialValue(value: AttributeInheritanceFormValue | null): void {
    if (!value) {
      return;
    }

    const initialSpecies = this.buildInitialSpecies(value);
    this.seedCatalogOption(
      this.standTypeOptionsMap,
      this.standTypeOptions,
      value.standTypeId,
      value.standTypeLabel,
    );
    this.seedCatalogOption(
      this.plantingTypeOptionsMap,
      this.plantingTypeOptions,
      value.plantingTypeId,
      value.plantingTypeLabel,
    );
    this.seedGeneticMaterialOption(value.geneticMaterialId, value.geneticMaterialLabel, initialSpecies);
    this.seedCatalogOption(this.seasonOptionsMap, this.seasonOptions, value.season, value.seasonLabel);
    this.seedCatalogOption(
      this.contractorOptionsMap,
      this.contractorOptions,
      value.contractor,
      value.contractorLabel,
    );
    this.seedCatalogOption(
      this.standStateOptionsMap,
      this.standStateOptions,
      value.standState,
      value.standStateLabel,
    );

    this.form.patchValue(
      {
        StandTypeId: value.standTypeId,
        HasNearbyPopulation: value.nearbyPopulation,
        CurrentRotation: value.rotation,
        PlantingDate: this.parseDate(value.plantingDate),
        PlantingType: value.plantingTypeId,
        GeneticMaterial: value.geneticMaterialId,
        Species: value.speciesLabel,
        Season: value.season,
        Contractor: value.contractor,
        StandState: value.standState,
      },
      { emitEvent: false },
    );

    this.selectedPlantationSpecies.set(initialSpecies);
  }

  private syncSelectedGeneticMaterialSpecies(): void {
    syncSpeciesWithGeneticMaterialFromForm({
      destroyRef: this.destroyRef,
      form: this.form,
      geneticMaterialOptions: this.geneticMaterialOptions,
      geneticMaterialsLoader: this.loadGeneticMaterialOptions,
      applySpeciesSelection: species => this.applySpeciesSelection(species),
      loading: this.loading,
      pendingRequestIds: this.pendingGeneticMaterialRequests,
    });
  }

  private normalizeLoaderParams(params?: ZnSelectQueryParams): ZnSelectQueryParams {
    return {
      ...params,
      maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
    };
  }

  private applySpeciesSelection(species: SpeciesDto | null): void {
    this.selectedPlantationSpecies.set(species ?? null);
    this.form.get('Species')?.patchValue(species?.name ?? '', { emitEvent: false });
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private seedCatalogOption(
    map: Map<string, SelectOption>,
    options: WritableSignal<SelectOption[]>,
    key: SelectOptionValue,
    value: string | null | undefined,
  ): void {
    if (key === null || key === undefined || key === '' || !value) {
      return;
    }

    map.set(String(key), {
      key: String(key),
      value,
    });
    options.set(Array.from(map.values()));
  }

  private mergeCatalogOptions(
    map: Map<string, SelectOption>,
    options: WritableSignal<SelectOption[]>,
    items: Array<{ key: SelectOptionKey; value: string }>,
  ): void {
    items.forEach(item => {
      map.set(String(item.key), {
        key: String(item.key),
        value: String(item.value),
      });
    });

    options.set(Array.from(map.values()));
  }

  private seedGeneticMaterialOption(
    key: SelectOptionValue,
    value: string | null | undefined,
    species: SpeciesDto | null,
  ): void {
    if (key === null || key === undefined || key === '' || !value) {
      return;
    }

    this.geneticMaterialOptionsMap.set(String(key), {
      key: String(key),
      value,
      species,
    });
    this.geneticMaterialOptions.set(Array.from(this.geneticMaterialOptionsMap.values()));
  }

  private mergeGeneticMaterialOptions(items: GeneticMaterialSelectOption[]): void {
    items.forEach(item => {
      this.geneticMaterialOptionsMap.set(String(item.key), {
        key: String(item.key),
        value: String(item.value),
        species: item.species ?? null,
      });
    });

    this.geneticMaterialOptions.set(Array.from(this.geneticMaterialOptionsMap.values()));
  }

  private buildInitialSpecies(value: AttributeInheritanceFormValue): SpeciesDto | null {
    if (!value.speciesId || !value.speciesLabel) {
      return null;
    }

    const speciesId = Number(value.speciesId);
    if (!Number.isFinite(speciesId)) {
      return null;
    }

    return {
      id: speciesId,
      name: value.speciesLabel,
    };
  }

  private resolveOptionLabel(
    options: ResolvableSelectOption[],
    value: SelectOptionValue,
    fallbackValue?: string,
  ): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    return (
      options.find(option => String(option.key) === String(value))?.value ??
      fallbackValue ??
      String(value)
    );
  }

  private resolveLocalizedOptionLabel(
    options: SelectOption[],
    value: SelectOptionValue,
    resource: ZendraLocalizationServices,
  ): string {
    const optionValue = this.resolveOptionLabel(options, value);

    if (!optionValue) {
      return '';
    }

    const key = optionValue.includes('::') ? optionValue : `${resource}::${optionValue}`;
    return this.localizationService.instant(key);
  }

  private normalizeString(value: SelectOptionValue): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return String(value);
  }

  private formatDate(value: NgbDateStruct | null): string {
    if (!value) {
      return '';
    }

    const day = String(value.day).padStart(2, '0');
    const month = String(value.month).padStart(2, '0');
    return `${day}/${month}/${value.year}`;
  }

  private parseDate(value: string | null | undefined): NgbDateStruct | null {
    if (!value) {
      return null;
    }

    const [day, month, year] = value.split('/').map(Number);
    if (!day || !month || !year) {
      return null;
    }

    return { day, month, year };
  }

  private resolveInitialOptionLabel(
    currentValue: SelectOptionValue,
    initialValue: SelectOptionValue,
    initialLabel: string,
  ): string | undefined {
    if (!initialLabel || currentValue === null || currentValue === undefined || currentValue === '') {
      return undefined;
    }

    return String(currentValue) === String(initialValue) ? initialLabel : undefined;
  }
}
